/**
 * Painel lateral de anotações da OS (timeline + envio).
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, Send } from 'lucide-react';
import { technicianInitials } from '../utils/technicianInitials';
import { useUserProfilePhotos } from '../../user-management/hooks/useUserProfilePhotos';

/** Exibe data já formatada pelo SQL (dd/MM/yyyy hh:mm) sem conversão de fuso. */
export function formatNoteDateTime(value) {
  const s = String(value ?? '').trim();
  return s || '—';
}

function NoteChatAvatar({ user, profilePhotoUrl }) {
  const photo = String(profilePhotoUrl ?? '').trim();
  if (photo) {
    return (
      <span className="order-details-notes-chat-avatar order-details-notes-chat-avatar--photo" aria-hidden>
        <img src={photo} alt="" className="order-details-notes-chat-avatar-img" />
      </span>
    );
  }
  return (
    <span className="order-details-notes-chat-avatar order-details-notes-chat-avatar--initials" aria-hidden>
      {technicianInitials(user)}
    </span>
  );
}

export function OrderDetailsNotesPanel({
  notes,
  loadingNotes,
  savingNote,
  notesError,
  saveError,
  onSubmit,
  floatingCompose = false,
  startComposeOpen = false,
}) {
  const [draft, setDraft] = useState('');
  const [composeOpen, setComposeOpen] = useState(Boolean(floatingCompose && startComposeOpen));
  const inputRef = useRef(null);

  const photoSubjects = useMemo(
    () =>
      (Array.isArray(notes) ? notes : [])
        .map((item) => ({
          id: item?.profilePhotoUserId,
          hasProfilePhoto: item?.hasProfilePhoto === true,
        }))
        .filter((x) => Number(x.id) > 0),
    [notes]
  );
  const photoMap = useUserProfilePhotos(photoSubjects);

  useEffect(() => {
    if (startComposeOpen) setComposeOpen(true);
  }, [startComposeOpen]);

  useEffect(() => {
    if (floatingCompose && composeOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [floatingCompose, composeOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const texto = draft.trim();
    if (!texto || savingNote) return;
    const ok = await onSubmit(texto);
    if (ok) {
      setDraft('');
      if (floatingCompose) setComposeOpen(false);
    }
  };

  const composeForm = (
    <form
      className={`order-details-notes-panel-compose${composeOpen ? ' order-details-notes-panel-compose--open' : ''}`}
      onSubmit={handleSubmit}
    >
      <textarea
        ref={inputRef}
        className="order-details-notes-panel-input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Digite uma anotação…"
        rows={3}
        disabled={savingNote}
      />
      {saveError ? (
        <p className="order-details-notes-panel-status order-details-notes-panel-status--error">{saveError}</p>
      ) : null}
      <button
        type="submit"
        className="order-details-notes-panel-send"
        disabled={savingNote || !draft.trim()}
      >
        <Send size={14} strokeWidth={2.25} aria-hidden />
        {savingNote ? 'Enviando…' : 'Enviar'}
      </button>
    </form>
  );

  return (
    <div
      className={`order-details-notes-panel-root${floatingCompose ? ' order-details-notes-panel-root--floating' : ''}`}
    >
      <div className="order-details-notes-panel-timeline">
        {loadingNotes ? (
          <p className="order-details-notes-panel-status">Carregando anotações…</p>
        ) : notesError ? (
          <p className="order-details-notes-panel-status order-details-notes-panel-status--error">{notesError}</p>
        ) : notes.length === 0 ? (
          <p className="order-details-notes-panel-status">Nenhuma anotação ainda.</p>
        ) : (
          <ul className="order-details-notes-chat-list">
            {notes.map((item, index) => {
              const user = item.TB02130_USER || '—';
              const key = `${user}-${formatNoteDateTime(item.TB02130_DATA)}-${index}`;
              const photoUrl =
                item?.profilePhotoUserId != null
                  ? photoMap[String(item.profilePhotoUserId)] || null
                  : null;
              return (
                <li key={key} className="order-details-notes-chat-item">
                  <NoteChatAvatar user={user} profilePhotoUrl={photoUrl} />
                  <div className="order-details-notes-chat-bubble">
                    <div className="order-details-notes-chat-meta">
                      <span className="order-details-notes-chat-user">{user}</span>
                      <span className="order-details-notes-chat-datetime">
                        {formatNoteDateTime(item.TB02130_DATA)}
                      </span>
                    </div>
                    <p className="order-details-notes-chat-text">{item.TB02130_OBS || '—'}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {floatingCompose ? (
        <div className="order-details-notes-panel-footer">
          {composeOpen ? (
            composeForm
          ) : (
            <button
              type="button"
              className="order-details-notes-add-fab"
              onClick={() => setComposeOpen(true)}
              aria-label="Adicionar anotação"
            >
              <span className="order-details-notes-add-fab-icon" aria-hidden>
                <Plus size={14} strokeWidth={2.25} />
              </span>
              <span className="order-details-notes-add-fab-label">Adicionar anotação</span>
            </button>
          )}
        </div>
      ) : (
        composeForm
      )}
    </div>
  );
}
